import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import './Profile.css';

function Profile() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [stats, setStats] = useState({ conversations_count: 0, hours_of_wisdom: 0 });
    const [loadingStats, setLoadingStats] = useState(true);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        // Load Razorpay Script
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);

        const fetchStats = async () => {
            if (!user?.id) return;
            try {
                // Using axios for consistency and better error handling
                const response = await axios.get(`${API_ENDPOINTS.USER_STATS}`, {
                    params: { user_id: user.id }
                });
                
                if (response.data.success) {
                    setStats(response.data.stats);
                }
            } catch (error) {
                console.error("Error fetching user stats:", error);
            } finally {
                setLoadingStats(false);
            }
        };

        fetchStats();
    }, [user]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleDeleteAccount = async () => {
        if (!user?.id) return;
        
        setIsDeleting(true);
        setDeleteError('');
        
        try {
            // Using the base API URL by removing '/ask' from the endpoint
            const baseUrl = API_ENDPOINTS.ASK.replace('/ask', '');
            const response = await axios.post(`${baseUrl}/user/delete`, {
                user_id: user.id
            });
            
            if (response.data.success) {
                // Success! Logout and redirect
                logout();
                // We'll use a small delay to ensure the user sees the success state if needed, 
                // but direct redirect is usually better for UX
                navigate('/login', { state: { message: 'Your account has been successfully deleted.' } });
            }
        } catch (error) {
            console.error("Error deleting account:", error);
            setDeleteError(error.response?.data?.error || 'Failed to delete account. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRedoMandate = (subscriptionId) => {
        if (!window.Razorpay || !stats.razorpay_key) {
            alert("Payment system not ready. Please try again.");
            return;
        }

        const options = {
            key: stats.razorpay_key,
            subscription_id: subscriptionId,
            name: "Talk to Krishna India",
            description: "Authorize Monthly Auto-debit",
            image: "/logo192.png",
            handler: async function (response) {
                console.log("Mandate authorized successfully!", response);
                try {
                    // Sync status with backend
                    const syncRes = await axios.post(API_ENDPOINTS.VERIFY_SUBSCRIPTION, {
                        subscription_id: subscriptionId,
                        user_id: user.id
                    });
                    
                    if (syncRes.data.success) {
                        // Refresh stats to show new active status
                        const refreshRes = await axios.get(API_ENDPOINTS.USER_STATS, { params: { user_id: user.id } });
                        if (refreshRes.data.success) setStats(refreshRes.data.stats);
                    }
                } catch (err) {
                    console.error("Error syncing subscription:", err);
                }
            },
            prefill: {
                name: user.name,
                email: user.email,
            },
            theme: {
                color: "#f4a261"
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
    };

    const handleCancelSubscription = async (subscriptionId) => {
        if (!window.confirm("Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.")) {
            return;
        }

        try {
            const response = await axios.post(API_ENDPOINTS.CANCEL_SUBSCRIPTION, {
                subscription_id: subscriptionId,
                user_id: user.id
            });

            if (response.data.success) {
                alert("Subscription cancelled successfully. It will not renew after the current cycle.");
                // Refresh stats
                const refreshRes = await axios.get(API_ENDPOINTS.USER_STATS, { params: { user_id: user.id } });
                if (refreshRes.data.success) setStats(refreshRes.data.stats);
            }
        } catch (err) {
            console.error("Error cancelling subscription:", err);
            alert("Failed to cancel subscription. Please contact support.");
        }
    };

    if (!user) {
        navigate('/login');
        return null;
    }

    // Get user initials for avatar
    const getInitials = (name) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Get member since date (from user creation or current date)
    const getMemberSince = () => {
        // In a real app, this would come from the database
        return new Date().toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <div className="page-container profile-page">
            <Navbar />

            <div className="profile-container">
                {/* Profile Header */}
                <div className="profile-header glass">
                    <div className="profile-avatar">
                        <div className="avatar-circle">
                            {getInitials(user.name)}
                        </div>
                        <div className="avatar-glow"></div>
                    </div>

                    <div className="profile-info">
                        <h1 className="profile-name">{user.name}</h1>
                        <p className="profile-email">{user.email}</p>
                        <p className="profile-member-since">
                            🕉️ Seeker since {getMemberSince()}
                        </p>
                    </div>
                </div>

                {/* Profile Stats */}
                <div className="profile-stats">
                    <div className="stat-card glass">
                        <div className="stat-icon">💬</div>
                        <div className="stat-value">{loadingStats ? '--' : stats.conversations_count}</div>
                        <div className="stat-label">Conversations</div>
                    </div>

                    <div className="stat-card glass">
                        <div className="stat-icon">⏱️</div>
                        <div className="stat-value">{loadingStats ? '--' : (stats.hours_of_wisdom % 1 === 0 ? stats.hours_of_wisdom : stats.hours_of_wisdom.toFixed(1))}</div>
                        <div className="stat-label">Hours of Wisdom</div>
                    </div>
                </div>

                {/* Subscription Management Card */}
                {!loadingStats && stats.subscription && (() => {
                    // Map raw plan_id to a human-friendly label
                    const planLabelMap = {
                        'basic':   'Monthly Plan',
                        'premium': 'Yearly Plan',
                    };
                    const planLabel = planLabelMap[stats.subscription.plan_id] || stats.subscription.plan_id || 'Monthly Plan';

                    return (
                    <div className="subscription-card glass animate-fade-in">
                        <div className="subscription-header">
                            <div className="subscription-title">
                                <span className="icon">💳</span>
                                <h3>{planLabel}</h3>
                            </div>
                            <span className={`status-badge ${stats.subscription.status}`}>
                                {(stats.subscription.status === 'active' || stats.subscription.status === 'authenticated') ? '● Active' : 
                                 (stats.subscription.status === 'pending_mandate' || stats.subscription.status === 'created') ? '⚠️ Mandate Incomplete' : 
                                 stats.subscription.status === 'cancelled' ? '❌ Cancelled' :
                                 stats.subscription.status}
                            </span>
                        </div>

                        <div className="subscription-body">
                            {(stats.subscription.status === 'active' || stats.subscription.status === 'authenticated') ? (
                                <>
                                <p className="status-note success">
                                    Your subscription is active. Next billing date: <strong>{stats.subscription.next_billing_at ? new Date(stats.subscription.next_billing_at).toLocaleDateString() : 'Next Cycle'}</strong>
                                </p>
                                <button 
                                    className="btn-cancel-subscription"
                                    onClick={() => handleCancelSubscription(stats.subscription.subscription_id)}
                                >
                                    Cancel Subscription
                                </button>
                                </>
                            ) : (stats.subscription.status === 'pending_mandate' || stats.subscription.status === 'created') ? (
                                <>
                                    <p className="status-note warning">
                                        Your payment status is pending mandate / authorization. <strong>Automatic renewal is not enabled.</strong>
                                    </p>
                                    <button 
                                        className="btn-enable-autopay"
                                        onClick={() => handleRedoMandate(stats.subscription.subscription_id)}
                                    >
                                        Complete Setup Now
                                    </button>
                                </>
                            ) : stats.subscription.status === 'cancelled' ? (
                                <p className="status-note danger">
                                    Your subscription has been cancelled and will not renew. You can purchase a new plan from the pricing page.
                                </p>
                            ) : null}
                        </div>
                    </div>
                    );
                })()}

                {/* Profile Actions */}
                <div className="profile-actions">
                    <div className="action-card glass">
                        <h3>Account Settings</h3>
                        <div className="action-list">
                            <button className="action-item" onClick={() => navigate('/chat')}>
                                <span className="action-icon">💬</span>
                                <span className="action-text">Continue Conversation</span>
                                <span className="action-arrow">→</span>
                            </button>

                            <button className="action-item" disabled>
                                <span className="action-icon">✏️</span>
                                <span className="action-text">Edit Profile</span>
                                <span className="action-badge">Coming Soon</span>
                            </button>

                            <button className="action-item" disabled>
                                <span className="action-icon">🔔</span>
                                <span className="action-text">Notifications</span>
                                <span className="action-badge">Coming Soon</span>
                            </button>

                            <button className="action-item" onClick={() => navigate('/reset-password')}>
                                <span className="action-icon">🔒</span>
                                <span className="action-text">Change Password</span>
                                <span className="action-arrow">→</span>
                            </button>
                        </div>
                    </div>

                    <div className="action-card glass danger-zone">
                        <h3>Danger Zone</h3>
                        <div className="action-list">
                            <button
                                className="action-item danger"
                                onClick={() => setShowLogoutConfirm(true)}
                            >
                                <span className="action-icon">🚪</span>
                                <span className="action-text">Logout</span>
                                <span className="action-arrow">→</span>
                            </button>

                            <button 
                                className="action-item danger" 
                                onClick={() => setShowDeleteConfirm(true)}
                            >
                                <span className="action-icon">🗑️</span>
                                <span className="action-text">Delete Account</span>
                                <span className="action-arrow">→</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Dialog */}
            {showLogoutConfirm && (
                <div className="confirm-overlay" onClick={() => setShowLogoutConfirm(false)}>
                    <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-icon logout-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h3>Logout Confirmation</h3>
                        <p>Are you sure you want to logout? You can always come back for more divine wisdom.</p>
                        <div className="confirm-actions">
                            <button className="btn-cancel" onClick={() => setShowLogoutConfirm(false)}>
                                Stay
                            </button>
                            <button className="btn-confirm logout-btn" onClick={handleLogout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Account Deletion Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className="confirm-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
                    <div className="confirm-dialog delete-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-icon delete-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h3>Delete Account?</h3>
                        <p>This action is irreversible. All your conversation history with Krishna will be preserved internally but you will no longer be able to access this account. Are you sure?</p>
                        
                        {deleteError && <p className="error-message" style={{ color: '#ef4444', marginBottom: '16px' }}>{deleteError}</p>}
                        
                        <div className="confirm-actions">
                            <button 
                                className="btn-cancel" 
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-confirm" 
                                onClick={handleDeleteAccount}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Profile;
